// src/dashboard/pages/ListAdminBookings/index.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const StatusBadge = ({ status }) => {
  const map = { pending: 'secondary', confirmed: 'success', rejected: 'danger' };
  return <span className={`badge bg-${map[status] || 'secondary'}`}>{status}</span>;
};

export default function AdminBookings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const authHeader = () => {
    const raw = localStorage.getItem('token_b2b') || '';
    return raw.startsWith('Bearer ') ? raw : `Bearer ${raw}`;
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`/api/admin/bookings`, {
          headers: { Authorization: authHeader() },
        });
        const d = res.data;
        const items = Array.isArray(d?.bookings) ? d.bookings : Array.isArray(d) ? d : [];
        setRows(
          items.map((r) => ({
            ...r,
            _edit: {
              discountType: r.discountType || '',
              discountValue: r.discountValue ?? '',
              adminNote: r.adminNote || '',
            },
          })),
        );
      } catch (e) {
        console.error('admin bookings load error:', e?.response?.data || e);
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const updateRowLocal = (id, patch) =>
    setRows((prev) => prev.map((r) => (r._id === id ? { ...r, ...patch } : r)));

  const setEdit = (id, patch) =>
    setRows((prev) =>
      prev.map((r) => (r._id === id ? { ...r, _edit: { ...r._edit, ...patch } } : r)),
    );

  const saveDiscount = async (row) => {
    const { discountType, discountValue } = row._edit;
    try {
      const { data } = await axios.patch(
        `/api/admin/bookings/${row._id}/discount`,
        {
          discountType: discountType || null,
          discountValue: discountValue ? Number(discountValue) : 0,
        },
        { headers: { Authorization: authHeader() } },
      );

      updateRowLocal(row._id, {
        ...data,
        _edit: {
          discountType: data.discountType || '',
          discountValue: data.discountValue ?? '',
          adminNote: data.adminNote || '',
        },
      });
    } catch (e) {
      console.error('save discount error:', e?.response?.data || e);
      alert('Échec de l’enregistrement de la remise');
    }
  };

  const updateStatus = async (row, newStatus) => {
    try {
      const { data } = await axios.patch(
        `/api/admin/bookings/${row._id}/status`,
        { status: newStatus, adminNote: row._edit.adminNote },
        { headers: { Authorization: authHeader() } },
      );
      updateRowLocal(row._id, {
        ...data,
        _edit: {
          discountType: data.discountType || '',
          discountValue: data.discountValue ?? '',
          adminNote: data.adminNote || '',
        },
      });
    } catch (e) {
      console.error('update status error:', e?.response?.data || e);
      alert('Échec de la mise à jour du statut');
    }
  };

  if (loading) return <p className="p-4">Chargement…</p>;

  return (
    <div className="py-4">
      <h2 className="mb-3">Réservations B2B</h2>

      <div className="table-responsive">
        <table className="table align-middle">
          <thead>
            <tr>
              <th>Client</th>
              <th>Produit</th>
              <th>Départ</th>
              <th>Prix base / pers</th>
              <th>Promo</th>
              <th>Prix total</th>
              <th>Statut</th>
              <th>Note admin</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((r) => {
              const base = Number(r.pricePerPerson || 0);
              const pax = Number(r.adults || 0) + Number(r.children || 0);
              const finalPer = Number(r.finalPricePerPerson ?? base);
              const total = Number(r.finalTotalPrice ?? finalPer * pax);

              return (
                <tr key={r._id}>
                  <td>
                    <div className="fw-semibold">
                      {r.userId?.companyName || r.user?.companyName || '—'}
                    </div>
                    <div className="text-muted small">{r.userId?.email || r.user?.email}</div>
                  </td>

                  <td>
                    <div className="fw-semibold">{r.productName || r.productId}</div>
                    <div className="text-muted small">
                      {r.destination?.city} / {r.destination?.country}
                    </div>
                  </td>

                  <td>
                    <div>{new Date(r.date).toLocaleDateString('fr-FR')}</div>
                    <div className="text-muted small">From: {r.from || '—'}</div>
                    <div className="text-muted small">
                      {r.adults} ad. / {r.children} enf.
                    </div>
                  </td>

                  <td>
                    <div className="fw-semibold">
                      {base.toFixed(2)} {r.currency}
                    </div>
                  </td>

                  <td style={{ minWidth: 240 }}>
                    <div className="d-flex gap-2">
                      <select
                        className="form-select"
                        value={r._edit.discountType}
                        onChange={(e) => setEdit(r._id, { discountType: e.target.value })}
                      >
                        <option value="">(aucune)</option>
                        <option value="amount">– € montant</option>
                        <option value="percent">– %</option>
                      </select>

                      <input
                        className="form-control"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="valeur"
                        value={r._edit.discountValue}
                        onChange={(e) => setEdit(r._id, { discountValue: e.target.value })}
                        style={{ maxWidth: 110 }}
                      />

                      <button
                        className="btn btn-outline-primary"
                        onClick={() => saveDiscount(r)}
                      >
                        Enr.
                      </button>
                    </div>
                  </td>

                  <td className="fw-bold">
                    {total.toFixed(2)} {r.currency}
                  </td>

                  <td>
                    <StatusBadge status={r.status} />
                  </td>

                  <td style={{ minWidth: 180 }}>
                    <input
                      className="form-control"
                      placeholder="Note admin…"
                      value={r._edit.adminNote}
                      onChange={(e) => setEdit(r._id, { adminNote: e.target.value })}
                    />
                  </td>

                  <td>
                    <div className="btn-group">
                      <button
                        className="btn btn-success"
                        onClick={() => updateStatus(r, 'confirmed')}
                      >
                        Confirmer
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => updateStatus(r, 'rejected')}
                      >
                        Refuser
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}

            {rows.length === 0 && (
              <tr>
                <td colSpan="9" className="text-center text-muted py-4">
                  Aucune réservation.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
